import localforage from 'localforage';
import type {
  AudioRecCalibrationProfile,
  RecognitionDetectedNote,
  RecognitionFrame,
  RecognitionPeak,
  RecognitionSuppressedNote,
} from '../types';
import type {
  LocalAudioBridgeEventDetail,
  LocalAudioGradeEventDetail,
} from 'Utils/LocalAudioSessionEvents';
import { serializeWaveformSnapshot } from './waveformCapture';

export const LOCAL_AUDIO_SESSION_MAX_FRAMES = 12_000;
export const LOCAL_AUDIO_SESSION_ENDPOINT = '/__museflow/audio-recognition-sessions';

export type LocalAudioExpectedSnapshot = {
  midiNotes: number[];
  expectationKey?: string;
};

export type LocalAudioSessionFrame = {
  capturedAtMs: number;
  audioTimeSec: number;
  exercisePlaying: boolean;
  expectationKey: string;
  expectedMidiNotes: number[];
  recognizedMidiNotes: number[];
  onsetMidiNotes: number[];
  rms: number;
  minRms: number;
  noiseFloor: number;
  residualEnergy: number | null;
  recognitionMode: string;
  detectedNotes: RecognitionDetectedNote[];
  selectedNotes: RecognitionDetectedNote[];
  suppressedNotes: RecognitionSuppressedNote[];
  peaks: RecognitionPeak[];
  waveform?: number[];
};

export type LocalAudioSessionSummary = {
  expectedEventCount: number;
  recognizedEventCount: number;
  missedAfterSignalCount: number;
  noSignalCount: number;
  recognizedFrameCount: number;
  onsetCount: number;
  bridgeNoteOnCount: number;
  bridgeNoteOffCount: number;
  waveformSnapshotCount: number;
  gradeCounts: Record<string, number>;
};

export type LocalAudioSessionReport = {
  schemaVersion: 1;
  source: 'museflow-local-audio-session';
  sessionId: string;
  createdAtIso: string;
  startedAtIso: string;
  endedAtIso: string;
  durationMs: number;
  frameCount: number;
  truncated: boolean;
  metadata: Record<string, unknown>;
  calibrationProfile: AudioRecCalibrationProfile | null;
  summary: LocalAudioSessionSummary;
  bridgeEvents: LocalAudioBridgeEventDetail[];
  gradeEvents: LocalAudioGradeEventDetail[];
  frames: LocalAudioSessionFrame[];
};

type BuildLocalAudioSessionReportOptions = {
  sessionId: string;
  startedAtMs: number;
  endedAtMs: number;
  truncated: boolean;
  metadata: Record<string, unknown>;
  calibrationProfile: AudioRecCalibrationProfile | null;
  bridgeEvents: LocalAudioBridgeEventDetail[];
  gradeEvents: LocalAudioGradeEventDetail[];
};

const fallbackStore = localforage.createInstance({
  name: 'museflow-local-diagnostics',
  storeName: 'audio_recognition_sessions',
});

const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((left, right) => left - right);

const notesEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((note, index) => note === right[index]);

const cloneDetectedNotes = (notes: RecognitionDetectedNote[] | undefined) =>
  (notes ?? []).map((note) => ({ ...note }));

const cloneSuppressedNotes = (notes: RecognitionSuppressedNote[] | undefined) =>
  (notes ?? []).map((note) => ({ ...note }));

const clonePeaks = (peaks: RecognitionPeak[] | undefined) =>
  (peaks ?? [])
    .slice()
    .sort((left, right) => right.amp - left.amp)
    .slice(0, 16)
    .map((peak) => ({ ...peak }));

export const buildLocalAudioSessionFrame = (
  frame: RecognitionFrame,
  expected: LocalAudioExpectedSnapshot,
  exercisePlaying: boolean,
  capturedAtMs = Date.now(),
  captureWaveform = false,
): LocalAudioSessionFrame => {
  const waveform = captureWaveform ? serializeWaveformSnapshot(frame.waveform) : undefined;
  return {
    capturedAtMs,
    audioTimeSec: frame.timeSec,
    exercisePlaying,
    expectationKey: expected.expectationKey ?? '',
    expectedMidiNotes: normalizeMidiNotes(expected.midiNotes),
    recognizedMidiNotes: normalizeMidiNotes(frame.midiNotes ?? []),
    onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes ?? []),
    rms: frame.rms ?? 0,
    minRms: frame.minRms ?? 0,
    noiseFloor: frame.noiseFloor ?? 0,
    residualEnergy: frame.residualEnergy ?? null,
    recognitionMode: frame.recognitionMode ?? 'default',
    detectedNotes: cloneDetectedNotes(frame.detectedNotes),
    selectedNotes: cloneDetectedNotes(frame.selectedNotes),
    suppressedNotes: cloneSuppressedNotes(frame.suppressedNotes),
    peaks: clonePeaks(frame.peaks),
    ...(waveform ? { waveform } : {}),
  };
};

type ExpectedEvent = {
  expectedMidiNotes: number[];
  frames: LocalAudioSessionFrame[];
};

const collectExpectedEvents = (frames: LocalAudioSessionFrame[]) => {
  const events: ExpectedEvent[] = [];
  let current: ExpectedEvent | null = null;
  let currentKey = '';

  for (const frame of frames) {
    const key =
      frame.exercisePlaying && frame.expectedMidiNotes.length > 0
        ? frame.expectationKey || `midi:${frame.expectedMidiNotes.join(',')}`
        : '';

    if (!key) {
      current = null;
      currentKey = '';
      continue;
    }

    if (!current || key !== currentKey) {
      current = {
        expectedMidiNotes: frame.expectedMidiNotes,
        frames: [],
      };
      events.push(current);
      currentKey = key;
    }
    current.frames.push(frame);
  }

  return events;
};

export const summarizeLocalAudioSession = (
  frames: LocalAudioSessionFrame[],
  bridgeEvents: LocalAudioBridgeEventDetail[],
  gradeEvents: LocalAudioGradeEventDetail[],
): LocalAudioSessionSummary => {
  const expectedEvents = collectExpectedEvents(frames);
  let recognizedEventCount = 0;
  let missedAfterSignalCount = 0;
  let noSignalCount = 0;

  for (const expectedEvent of expectedEvents) {
    const hasSignal = expectedEvent.frames.some(
      (frame) => frame.minRms > 0 && frame.rms >= frame.minRms,
    );
    const recognized = expectedEvent.frames.some((frame) =>
      notesEqual(frame.expectedMidiNotes, frame.recognizedMidiNotes),
    );
    if (recognized) {
      recognizedEventCount += 1;
    } else if (hasSignal) {
      missedAfterSignalCount += 1;
    } else {
      noSignalCount += 1;
    }
  }

  const gradeCounts = gradeEvents.reduce<Record<string, number>>((counts, event) => {
    counts[event.state] = (counts[event.state] ?? 0) + 1;
    return counts;
  }, {});

  return {
    expectedEventCount: expectedEvents.length,
    recognizedEventCount,
    missedAfterSignalCount,
    noSignalCount,
    recognizedFrameCount: frames.filter((frame) => frame.recognizedMidiNotes.length > 0).length,
    onsetCount: frames.reduce((count, frame) => count + frame.onsetMidiNotes.length, 0),
    bridgeNoteOnCount: bridgeEvents.filter((event) => event.type === 'note-on').length,
    bridgeNoteOffCount: bridgeEvents.filter((event) => event.type === 'note-off').length,
    waveformSnapshotCount: frames.filter((frame) => frame.waveform != null).length,
    gradeCounts,
  };
};

export const buildLocalAudioSessionReport = (
  frames: LocalAudioSessionFrame[],
  options: BuildLocalAudioSessionReportOptions,
): LocalAudioSessionReport => ({
  schemaVersion: 1,
  source: 'museflow-local-audio-session',
  sessionId: options.sessionId,
  createdAtIso: new Date(options.endedAtMs).toISOString(),
  startedAtIso: new Date(options.startedAtMs).toISOString(),
  endedAtIso: new Date(options.endedAtMs).toISOString(),
  durationMs: Math.max(0, options.endedAtMs - options.startedAtMs),
  frameCount: frames.length,
  truncated: options.truncated,
  metadata: options.metadata,
  calibrationProfile: options.calibrationProfile,
  summary: summarizeLocalAudioSession(frames, options.bridgeEvents, options.gradeEvents),
  bridgeEvents: options.bridgeEvents,
  gradeEvents: options.gradeEvents,
  frames,
});

const saveFallback = async (report: LocalAudioSessionReport) => {
  await fallbackStore.setItem('latest', report);
  await fallbackStore.setItem(`session:${report.sessionId}`, report);
};

export const persistLocalAudioSession = async (
  report: LocalAudioSessionReport,
  keepalive = false,
) => {
  try {
    await saveFallback(report);
  } catch (error) {
    console.warn('[AudioRecognition] Could not save the local session in browser storage.', error);
  }

  const response = await fetch(LOCAL_AUDIO_SESSION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    keepalive,
  });
  if (!response.ok) {
    throw new Error(`Local audio session save failed with status ${response.status}.`);
  }
};

export const getLatestLocalAudioSession = () =>
  fallbackStore.getItem<LocalAudioSessionReport>('latest');
